import { useMemo } from "react";
import { useForm } from "@tanstack/react-form";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { RequiredInput } from "@/components/common/required-input";
import { TIME_OPTIONS } from "@/constants/staff/staff-constants";
import { getStaffRosterOverrideFormValues } from "@/default-form-values/staff/default-staff-roster-override-form-values";
import {
  useCreateStaffRosterOverride,
  useDeleteStaffRosterOverride,
  useGetStaffRosterOverrides,
  useUpdateStaffRosterOverride,
} from "@/hooks/staff/use-staff-roster-overrides";
import {
  toStaffRosterOverrideCreate,
  toStaffRosterOverrideUpdate,
} from "@/mappers/staff/staff-roster-override-form-to-request";
import type { TimeRange } from "@/types/appointments/api-types/availability-types";
import { staffRosterOverrideSchema } from "@/zod-schemas/staff/staff-roster-override-schema";
import { useIntl } from "react-intl";
import { isoTime, toIsoDate } from "@/lib/utils";

interface OverrideRosterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staffId: number;
  staffName: string;
  date: Date;
  workingHours: TimeRange[];
}

export function OverrideRosterDialog({
  open,
  onOpenChange,
  staffId,
  staffName,
  date,
  workingHours,
}: OverrideRosterDialogProps) {
  const intl = useIntl();
  const overrideDate = toIsoDate(date);
  const displayDate = intl.formatDate(date, { format: "long" });
  const lookupParams = useMemo(
    () => ({
      staff_id: staffId,
      override_date: overrideDate,
      page: 0,
      size: 1,
    }),
    [staffId, overrideDate]
  );
  const lookup = useGetStaffRosterOverrides(lookupParams, open);
  const existing = lookup.data?.items[0];

  const createOverride = useCreateStaffRosterOverride();
  const updateOverride = useUpdateStaffRosterOverride();
  const deleteOverride = useDeleteStaffRosterOverride();
  const isMutating =
    createOverride.isPending ||
    updateOverride.isPending ||
    deleteOverride.isPending;

  const form = useForm({
    defaultValues: getStaffRosterOverrideFormValues(workingHours),
    validators: {
      onChange: staffRosterOverrideSchema,
      onSubmit: staffRosterOverrideSchema,
    },
    onSubmit: async ({ value }) => {
      const validated = staffRosterOverrideSchema.parse(value);
      try {
        if (existing) {
          await updateOverride.mutateAsync({
            id: existing.id,
            data: toStaffRosterOverrideUpdate(validated),
          });
        } else {
          await createOverride.mutateAsync(
            toStaffRosterOverrideCreate(validated, staffId, overrideDate)
          );
        }
        toast.success(`${staffName}'s roster updated for ${displayDate}`);
      } catch {
        toast.error(`Failed to update roster for ${staffName}`);
        return;
      }
      onOpenChange(false);
    },
  });

  const handleRevert = async () => {
    if (!existing) return;
    try {
      await deleteOverride.mutateAsync(existing.id);
      toast.success(`${staffName}'s roster reverted for ${displayDate}`);
    } catch {
      toast.error(`Failed to revert roster for ${staffName}`);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[425px]">
        <DialogHeader className="shrink-0">
          <DialogTitle>Override Roster for {staffName}</DialogTitle>
        </DialogHeader>
        <Separator className="shrink-0" />
        <Field className="shrink-0">
          <FieldLabel htmlFor="override-roster-date">
            Date
            <RequiredInput />
          </FieldLabel>
          <Input id="override-roster-date" value={displayDate} disabled />
        </Field>
        {lookup.isPending ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : lookup.isError ? (
          <FieldError>Could not load roster override</FieldError>
        ) : lookup.isSuccess ? (
          <>
            <form
              id="override-roster-form"
              className="min-h-0 overflow-y-auto"
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <FieldGroup>
                <form.Field
                  name="is_working"
                  children={(workingField) => {
                    const isWorking = workingField.state.value;
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={isWorking}
                              onCheckedChange={(checked) =>
                                workingField.handleChange(checked)
                              }
                            />
                            <Label className="font-medium">Working</Label>
                          </div>
                          <form.Field
                            name="start_time"
                            children={(field) => (
                              <Select
                                value={field.state.value}
                                onValueChange={(value) =>
                                  field.handleChange(value)
                                }
                                disabled={!isWorking}
                              >
                                <SelectTrigger className="w-[130px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {TIME_OPTIONS.map((time) => (
                                    <SelectItem key={time} value={time}>
                                      {intl.formatTime(isoTime(time), { format: "clock" })}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          <span className="text-sm text-muted-foreground">
                            to
                          </span>
                          <form.Field
                            name="end_time"
                            children={(field) => (
                              <Select
                                value={field.state.value}
                                onValueChange={(value) =>
                                  field.handleChange(value)
                                }
                                disabled={!isWorking}
                              >
                                <SelectTrigger className="w-[130px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {TIME_OPTIONS.map((time) => (
                                    <SelectItem key={time} value={time}>
                                      {intl.formatTime(isoTime(time), { format: "clock" })}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>
                        <form.Subscribe
                          selector={(state) => state.fieldMeta.end_time}
                          children={(meta) =>
                            meta && !meta.isValid ? (
                              <FieldError errors={meta.errors} />
                            ) : null
                          }
                        />
                      </div>
                    );
                  }}
                />
              </FieldGroup>
            </form>
            <DialogFooter className="shrink-0 sm:justify-between">
              {existing ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRevert}
                  disabled={isMutating}
                >
                  Revert to roster
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline" className="w-24">
                    Cancel
                  </Button>
                </DialogClose>
                <form.Subscribe
                  selector={(state) =>
                    [state.canSubmit, state.isSubmitting] as const
                  }
                  children={([canSubmit, isSubmitting]) => (
                    <Button
                      type="submit"
                      form="override-roster-form"
                      className="w-24"
                      disabled={!canSubmit || isSubmitting || isMutating}
                    >
                      Save
                    </Button>
                  )}
                />
              </div>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
